import Image from "next/image";
import { Clock3, MapPin, Phone } from "lucide-react";

import type { BlockProps, StrapiBlock } from "@/app/components/blocks/types";
import { Paragraph } from "../design-system/atoms/Paragraph";
import { useAgency } from "../context/AgencyContext";

type PresentationAgenceBlock = {

    titre?: string | null;
    description?: string | null;
    image?: {
        url?: string | null;
        alternativeText?: string | null;
    } | null;
    adresse?: string | null;
    telephone?: string | null;
    horaire?: string | null;
};

export default function PresentationAgence({ data, }: BlockProps<PresentationAgenceBlock>) {

    const { apiBackend } = useAgency();

    const { titre, description, image, } = data;

    const adresse = apiBackend?.Adresse;

    const telephone = apiBackend?.Telephone;

    const horaire = apiBackend?.Schedule;


    const imageUrl = image?.url || "";
    const imageAlt = image?.alternativeText || titre || "Agence";

    return (
        <section className="py-12 lg:py-20">
            <div className="lms-container">
                <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">

                    {/* IMAGE */}
                    <div className="relative mx-auto w-full max-w-[500px]">
                        {/* Cercle décoratif vert pastel */}
                        <div className="absolute -bottom-1 -right-8 h-[180px] w-[180px] rounded-full bg-greenPastel lg:h-[210px] lg:w-[210px]" />

                        {/* Cercle extérieur : bordure verte + espace blanc */}
                        <div className="relative z-10 aspect-square rounded-full border-[5px] border-primaryGreen bg-transparent p-[14px]">

                            {/* Photo */}
                            <div className="relative h-full w-full overflow-hidden rounded-full">
                                {imageUrl && (
                                    <Image
                                        src={imageUrl}
                                        alt={imageAlt}
                                        fill
                                        className="object-cover"
                                    />
                                )}
                            </div>

                        </div>
                    </div>

                    {/* CONTENU */}
                    <div className="max-w-[560px]">
                        {titre && (
                            <h2 className="mb-7 text-4xl lg:text-[44px] font-bold leading-tight text-primaryGreen font-playfair ">
                                {titre}
                            </h2>

                        )}

                        {description && (
                            <Paragraph className="mb-8 text-lg leading-relaxed text-textColor">
                                {description}
                            </Paragraph>
                        )}

                        {/* INFORMATIONS */}
                        <div className="space-y-5 ">
                            {adresse && (
                                <div className="flex items-center gap-4">
                                    <Image
                                        src="https://site-lms.fra1.digitaloceanspaces.com/carte_des_broches_1_3365f6f4ad.png"
                                        alt="Adresse"
                                        width={34}
                                        height={34}
                                        className="shrink-0 text-primaryGreen"
                                    />

                                    <span className="h-7 w-[2px] bg-primaryGreen" />

                                    <p className="font-semibold text-textColor text-lg">
                                        {adresse}
                                    </p>
                                </div>
                            )}

                            {telephone && (
                                <div className="flex items-center gap-4">
                                    <Image
                                        src="https://site-lms.fra1.digitaloceanspaces.com/g1707_fcd094245f.png"
                                        alt="Téléphone"
                                        width={34}
                                        height={34}
                                        className="shrink-0 text-primaryGreen"
                                    />

                                    <span className="h-7 w-[2px] bg-primaryGreen" />

                                    <a
                                        href={`tel:${telephone.replace(/\s/g, "")}`}
                                        className="font-semibold text-textColor transition-colors text-lg hover:text-primaryGreen"
                                    >
                                        {telephone}
                                    </a>
                                </div>
                            )}

                            {horaire && (
                                <div className="flex items-center gap-4">
                                    <Image
                                        src="https://site-lms.fra1.digitaloceanspaces.com/g2454_ede3dcd6f0.png"
                                        alt="Horaire"
                                        width={34}
                                        height={34}
                                        className="shrink-0 text-primaryGreen"
                                    />

                                    <span className="h-7 w-[2px] bg-primaryGreen" />

                                    <p className="font-semibold text-lg text-textColor">
                                        {horaire}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}